import { KeyboardEvent, useEffect } from "react";
import { RemoveScroll } from "react-remove-scroll";
import AlertComponent from "./AlertComponent";
import { TranslatedText } from "../../../utilities/TranslatedText";

interface IOverlayComponentProps {
  alerts: { id: string | number; message: string }[];
  handleDismiss: (data: any) => void;
}

export default function OverlayComponent(props: IOverlayComponentProps) {
  useEffect(() => {
    window.scroll({ top: 0 });
  }, []);

  function dismiss() {
    props.handleDismiss(Object.assign({}, props, { type: "overlay" }));
  }

  // nothing behind the overlay is reachable while it is up, so Tab wraps at the
  // edges of the sheet instead of walking into the page underneath
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") return dismiss();
    if (e.key !== "Tab") return;
    const focusable = e.currentTarget.querySelectorAll<HTMLElement>(
      'a[href], button, [tabindex="0"]',
    );
    const edge = e.shiftKey ? 0 : focusable.length - 1;
    if (document.activeElement === focusable[edge]) {
      e.preventDefault();
      focusable[e.shiftKey ? focusable.length - 1 : 0].focus();
    }
  }

  const { alerts } = props;

  return (
    // RemoveScroll locks the page behind the overlay and leaves this element as
    // the only scroller. body { overflow: hidden } alone does not hold on iOS Safari.
    // forwardProps puts its handlers on the div below instead of adding a wrapper.
    <RemoveScroll forwardProps allowPinchZoom>
      <div
        className="sb-alert-overlay"
        role="alertdialog"
        onKeyDown={onKeyDown}
        aria-label="Alerts"
        aria-describedby="sb_overlay_message"
      >
        <div className={"sb-alert-overlay-box"}>
          <div
            id="sb_overlay_message"
            className="sb-box-message"
            tabIndex={0} // the message is the scroll region, so it has to be focusable
            role="document"
          >
            {alerts.map((alert) => (
              <AlertComponent key={alert.id} message={alert.message} />
            ))}
          </div>
          <div className="sb-box-action">
            <button
              ref={(el) => {
                if (el) el.focus();
              }}
              onClick={dismiss}
            >
              <TranslatedText>Continue</TranslatedText>
            </button>
          </div>
        </div>
      </div>
    </RemoveScroll>
  );
}
